import { FC, useState, useEffect } from 'react';
import successSignUpIcon from '../../assets/svg/successSignUpIcon.svg';
import styles from './page-supplier-signup.module.scss';
import Field from '../../components/shared/Field/Field';
import _ from 'lodash';
import BtButton from '../bt-button/bt-button';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { NotificationManager } from "react-notifications";
import * as Sentry from "@sentry/react";
import { serviceSalesOrgApplication } from '../../services/salesOrgApplicationService';
import { ReactComponent as CloseIcon } from '../../assets/svg/CloseIcon.svg';
import { AxiosError } from 'axios';
import { useTranslation, Trans } from 'react-i18next';
import { LeftBarWrapper } from './LeftBarWrapper';
import { logEvent } from '../../services/eventsService';
import { useLocation } from 'react-router-dom';
import appState from '../../state/AppStateContainer';
import LanguageSwitcher from '../../controls/LanguageSwitcher/LanguageSwitcher';

type Recipient = {
  personalNumber: string,
  name: string,
  email: string,
  tempId: number,
}

const newRecipient = () => ({
  personalNumber: '',
  name: '',
  email: '',
  tempId: Date.now(),
});

type ErrorMessages = {
  organizationNumber?: string,
  adminEmail?: string,
  recipients?: Partial<Recipient>[]
}

const SupplierSignUp: FC = () => {
  const [t] = useTranslation('newSupplier', { keyPrefix: 'signUp' });
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [organizationNumber, setOrganizationNumber] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [recipients, setRecipients] = useState<Recipient[]>([newRecipient()]);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [loadingOrgName, setLoadingOrgName] = useState(false);
  const [organizationNumberWarning, setOrganizationNumberWarning] = useState('');
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const source = query.get('source') ?? undefined;
  const consent = document.cookie.split("; ").find(row => row.startsWith('accuratorConsent' + "="))?.split("=")[1];
  const shouldRequireConsent = source === 'facebook';
  const canTrack = !shouldRequireConsent || consent === 'true';

  const uuid = query.get('uuid') ?? appState.getOrCreateAnonymousUUID() ?? undefined;
  const productId = query.get('product-id')  ?? undefined;

  useEffect(() => {
    logEvent({
      event: 'VISITED_SUPPLIER_SIGNUP',
      userIdentifier: uuid,
      metadata: {
        source: productId ? 'iframe' : (source || undefined),
        productId,
      },
      options: { unique: true },
    });

    if (shouldRequireConsent && !canTrack) {
      setShowConsentBanner(true);
    }
  }, []);

  const resetForm = () => {
    setSubmitted(false);
    setOrganizationNumber('');
    setAdminEmail('');
    setRecipients([newRecipient()]);
    setErrorMessages({});
    setOrganizationName('');
    setOrganizationNumberWarning('');
  };

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        resetForm();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  const submit = async () => {
    setSubmitting(true);
    setErrorMessages({});

    try {
      const trackingInfo: { source?: string, userIdentifier?: string, userId?: string } = {
        source: productId ? 'iframe' : (source || undefined),
      };
      if (!appState.getHvdToken()) {
        trackingInfo.userIdentifier = uuid;
      } else {
        trackingInfo.userId = appState.getUserId();
      }
      const { errorMessages } = await serviceSalesOrgApplication.createSalesOrgApplication({
        organizationNumber,
        adminEmail,
        recipients,
        trackingInfo,
      });
      if (!_.isEmpty(errorMessages)) {
        setErrorMessages(errorMessages);
        NotificationManager.error(
          t('notification.error.validation.description', 'Ogiltiga fält'),
          t('notification.error.validation.title', 'Ogiltig'),
        );
      } else {
        if (canTrack) {
          if (window.fbq) {
            window.fbq('trackCustom', 'CreatedSalesOrgApplication');
          }
        }
        
        setOrganizationNumber('');
        setAdminEmail('');
        setOrganizationName('');
        setRecipients([newRecipient()]);
        NotificationManager.success(
          t('notification.success.description', 'Din ansökan har lämnats'),
          t('notification.success.title', 'Ansökt'),
        );
        const url = new URL(window.location.href);
        url.searchParams.delete("uuid");
        url.searchParams.delete("product-id");
        url.searchParams.delete("source");
        window.history.replaceState({}, "", url);
        setSubmitted(true);
      }
    } catch (error) {
      if ((error as AxiosError<{error: string}>)?.response?.data?.error === 'error.notAllowedToSign') {
        NotificationManager.error(
          t('notification.error.notAllowedToSign.description', 'Firmateckningsrätt saknas. Lägg till VD eller registrerade firmatecknare.'),
          t('notification.error.notAllowedToSign.title', 'Misslyckat'),
        );
      } else {
        const errorMsg = _.get(error, 'response.data.error', t('notification.error.unexpected.description', 'Oväntat fel ({{error}})', { error }));
        NotificationManager.error(errorMsg, t('notification.error.unexpected.title', 'Misslyckat'));
        Sentry.captureException(error);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const lookupOrgNumber = async (organizationNumber: string) => {
    if (organizationNumber) {
      setLoadingOrgName(true);
      setOrganizationName('');
      try {
        const response = await serviceSalesOrgApplication.checkOrgNumber(organizationNumber);
        const orgName = response.name;
        setOrganizationName(orgName);
        setErrorMessages(prevState => ({ ...prevState, organizationNumber: '' }));
        setOrganizationNumberWarning('');
        return orgName;
      } catch (error) {
        setOrganizationNumberWarning('');
        if ((error as AxiosError<{error: string}>)?.response?.data?.error === 'error.invalidOrgNumber') {
          setErrorMessages(prevState => ({ ...prevState, organizationNumber: t('errorMessage.invalidOrgNumber', 'Ogiltigt organisationsnummer') }));
        } else if ((error as AxiosError<{error: string}>)?.response?.data?.error === 'error.canNotParseSigningRules') {
          setOrganizationNumberWarning(t('errorMessage.canNotParseSigningRules', 'Kunde inte tolka firmateckningsregler. Systemet förstod inte vem som tecknar firman för detta organisationsnummer. Ansökan kommer granskas manuellt inom tre arbetsdagar.'));
        } else if ((error as AxiosError<{error: string}>)?.response?.data?.error === 'error.orgExists') {
          setErrorMessages(prevState => ({ ...prevState, organizationNumber: t('errorMessage.orgExists', 'En organisation med det organisationsnumret finns redan') }));
        } else if ((error as AxiosError<{error: string}>)?.response?.data?.error === 'error.company.notFound') {
          setErrorMessages(prevState => ({ ...prevState, organizationNumber: t('errorMessage.notFound', 'Inget företag med detta organisationsnummer hittades.') }));
        } else if ((error as AxiosError<{error: string}>)?.response?.data?.error === 'error.service.unavailable') {
          setErrorMessages(prevState => ({ ...prevState, organizationNumber: t('errorMessage.unavailable', 'Något gick fel med anslutningen till vår kreditupplysningstjänst, så vi kunde inte slå upp organisationsnumret.') }));
        } else {
          const errorMsg = _.get(error, 'response.data.error', t('notification.error.unexpected.description', 'Oväntat fel ({{error}})', { error }));
          NotificationManager.error(errorMsg, t('notification.error.unexpected.title', 'Misslyckat'));
          Sentry.captureException(error);
        }
      } finally {
        setLoadingOrgName(false);
      }
    }
  }

  const formatTaxNumber = (oldN:string, newN: string) => {
    if (oldN.split('-').length < newN.split('-').length && oldN?.includes('-')) {
      return oldN;
    }
    if (newN.length > 11) return oldN;
    const wantsToRemoveDash = !newN.includes('-') && oldN?.includes('-');

    if (newN.length == 6 && !wantsToRemoveDash && !newN?.includes('-')) {
      newN = newN + '-';
    } else if (newN.length >= 7 && !newN?.includes('-')) {
      newN = newN.substring(0, 6) + '-' + newN.substring(6, newN.length);
    }

    return newN;
  }

  const translateErrorMessage = (errorMessage: string | undefined) => {
    if (!errorMessage) {
      return '';
    }
    switch (errorMessage) {
      case 'error.organizationNumberRequired':
        return t('errorMessage.organizationNumberRequired', 'Obligatoriskt');
      case 'error.organizationNumberInvalid':
        return t('errorMessage.organizationNumberInvalid', 'Ogiltigt organisationsnummer');
      case 'error.adminEmailRequired':
        return t('errorMessage.adminEmailRequired', 'Obligatoriskt');
      case 'error.personalNumberRequired':
        return t('errorMessage.personalNumberRequired', 'Obligatoriskt');
      case 'error.personalNumberInvalid':
        return t('errorMessage.personalNumberInvalid', 'Ogiltigt personnummer');
      case 'error.nameRequired':
        return t('errorMessage.nameRequired', 'Obligatoriskt');
      case 'error.emailRequired':
        return t('errorMessage.emailRequired', 'Obligatoriskt');
      case 'error.invalidEmail':
        return t('errorMessage.invalidEmail', 'Ogiltig e-postadress');
      default:
        return errorMessage;
    }
  }

  return (
    <div>
      <div className={styles.languageSwitcher}><LanguageSwitcher /></div>
      <LeftBarWrapper>
        <div className={styles.formContainer}>
          {!submitted && (
            <>
              <div className={styles.formTitle}>{t('form.title', 'Skapa ert kostnadsfria leverantörskonto')}</div>
              <div className={styles.introText}>
                <p>{t('form.intro', 'För att skapa ett företagskonto krävs signering med BankID.')}</p>
                <ul>
                  <li>{t('form.rule1', 'Om VD eller firmatecknare signerar godkänns ansökan direkt.')}</li>
                  <li>{t('form.rule2', 'Om någon annan signerar görs manuell granskning (upp till 3 arbetsdagar).')}</li>
                  <li>{t('form.rule3', 'När ansökan är godkänd får administratören en inbjudan via e-post.')}</li>
                </ul>
              </div>
              <div className={styles.form}>
                <Field
                  label={t('label.orgNo', 'Organisationsnummer')}
                  value={organizationNumber}
                  required
                  errorMessage={translateErrorMessage(errorMessages.organizationNumber)}
                  warningMessage={organizationNumberWarning}
                  onChange={value => {
                    setOrganizationNumber(prevState => {
                      const orgVal = formatTaxNumber(prevState, value.replace(/[^0-9-]/, '')?.trim());
                      if (orgVal.length == 11) {
                        lookupOrgNumber(orgVal);
                      } else {
                        setOrganizationName("");
                        setErrorMessages(prevState => ({ ...prevState, organizationNumber: '' }));
                      }
                      return orgVal;
                    });
                  }}
                />
              
                <div>
                  <div className={styles.fieldLabel}>{t('title.organizationName', 'Företagsnamn')}</div>
                  <span className={styles.organizationName}>
                    {organizationName}
                    {loadingOrgName ? (
                      <div className={styles.spinner}><div></div><div></div></div>
                    ) : (
                      !organizationName && <span className={styles.placeholder}>{t('placeholder.organizationName', '(hämtas automatiskt)')}</span>
                    )}
                  </span>
                </div>

                <Field 
                  label={t('label.adminEmail', 'Administratörens e-postadress')} 
                  value={adminEmail} 
                  onChange={setAdminEmail} 
                  required 
                  errorMessage={translateErrorMessage(errorMessages.adminEmail)} 
                />

                {!_.isEmpty(recipients) && (
                  <div className={styles.signingSection}>
                    <div className={styles.sectionTitle}>{t('signing.title', 'Signering')}</div>
                    <p className={styles.sectionDescription}>{t('signing.intro', 'Ange nedan vilka som ska skriva på användaravtalet.')}</p>
                    
                    <div className={styles.recipients}>
                      {recipients.map((recipient, index) => {
                        return (
                          <div className={styles.recipient} key={recipient.tempId}>
                            {recipients.length > 1 && <CloseIcon onClick={() => setRecipients(prevState => prevState.filter((_, i) => i !== index))} />}
                            <Field
                              label={t('label.personalNumber', 'Personnummer')}
                              value={recipient.personalNumber}
                              placeholder={t('placeholder.personalNumber', 'ÅÅMMDD-XXXX')}
                              required
                              errorMessage={translateErrorMessage(errorMessages.recipients?.[index]?.personalNumber)} 
                              className={styles.inlineField}
                              onChange={value => {
                                setRecipients(prevState => {
                                  const newState = [...prevState];
                                  const personalNumber = formatTaxNumber(prevState[index].personalNumber, value.replace(/[^0-9-]/, '').trim());

                                  newState[index].personalNumber = personalNumber;
                                  return newState;
                                });
                              }}
                            />
                            <Field
                              label={t('label.name', 'Namn')}
                              required
                              errorMessage={translateErrorMessage(errorMessages.recipients?.[index]?.name)} 
                              value={recipient.name}
                              className={styles.inlineField}
                              onChange={value => {
                                setRecipients(prevState => {
                                  const newState = [...prevState];
                                  newState[index].name = value;
                                  return newState;
                                })
                              }}
                            />
                            <Field
                              label={t('label.email', 'E-postadress')}
                              required
                              errorMessage={translateErrorMessage(errorMessages.recipients?.[index]?.email)} 
                              value={recipient.email}
                              className={styles.inlineField}
                              onChange={value => {
                                setRecipients(prevState => {
                                  const newState = [...prevState];
                                  newState[index].email = value;
                                  return newState;
                                })
                              }}
                              
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              
                <div className={styles.buttonGroup}>
                  <BtButton
                    color='white'
                    size='xxs'
                    leftIcon={<AddCircleIcon htmlColor="var(--orange-500)"/>}
                    style={{ width: 'fit-content' }}
                    onClick={() => setRecipients(prevState => [...prevState, newRecipient()])}
                  >
                    {t('button.addSignee', 'Lägg till undertecknare')}
                  </BtButton>
                  
                  <BtButton
                    loaderShow={submitting}
                    onClick={submit}
                    style={{ width: 'fit-content', backgroundColor: '#FF6B35', color: 'white', border: 'none' }}
                  >
                    {t('button.submit', 'Skicka in')}
                  </BtButton>
                </div>
            
                <p className={styles.readMore}>
                  <Trans t={t} i18nKey="readMore" components={{a: <a />}}/>
                </p>
              </div>
            </>
          )}
          {submitted && (
            <div className={styles.submittedForm}>
              <img src={successSignUpIcon} alt={t('imageAlt.successSignUpIcon', 'Success Sign Up Icon')} />
              
              <h2 className={styles.successHeading}>{t('submitted.heading', 'Ansökan inskickad!')}</h2>
              <p className={styles.successMessage}>
                {t('submitted.body', 'Din ansökan är inskickad och användaravtalet har skickats för signering. Administratören har fått en länk via e-post för att följa signeringsprocessen.')}
              </p>
              <button className={styles.goToStartButton} onClick={resetForm}>
                {t('submitted.button', 'Skapa ny ansökan')}
              </button>
            </div>
          )}
        </div>
      </LeftBarWrapper>
      <div className={`${styles.consentBanner} ${showConsentBanner && styles.show}`}>
        <div>
          <p>{t('consent.bannerHeader', 'Information om datainsamling')}</p>
          <p>{t('consent.bannerText1', 'Vi samlar in händelsebaserad användningsdata (t.ex. sidbesök och interaktioner) för analys och tjänsteförbättring.')}</p>
          <p>{t('consent.bannerText2', 'Data kan behandlas av tredje part. Inga cookies används.')}</p>
        </div>
        <div>
          <BtButton color='white' onClick={() => setShowConsentBanner(false)}>{t('consent.bannerDecline', 'Nedgång')}</BtButton>
          <BtButton onClick={() => {
            setShowConsentBanner(false);
            
            if (window.fbq) {
              window.fbq('consent', 'grant');
              const endOfNextDay = new Date();
              endOfNextDay.setDate(endOfNextDay.getDate() + 1);
              endOfNextDay.setHours(23, 59, 59, 999);

              document.cookie =
                `accuratorConsent=true; ` +
                `expires=${endOfNextDay.toUTCString()}; ` +
                `path=/supplier-sign-up`;
              window.fbq('trackCustom', 'VisitedSupplierSignup');
            }
          }}>{t('consent.bannerAccept', 'Acceptera')}</BtButton>
        </div>
      </div>
    </div>
  )
};

export default SupplierSignUp;
